export interface OfferBreakdownEntry {
  grossAmount?: number;
  mrp?: number;
  discountAmount?: number;
  discountRate?: number;
  baseCommissionAmount?: number;
  commissionAmount?: number;
  commissionRate?: number;
  netPayable?: number;
  metadata?: Record<string, unknown> | null;
}

export interface OfferBreakdown {
  listPrice: number;
  customerPaid: number;
  offerDiscount: number;
  offerDiscountRate: number;
  vendorCommission: number;
  baseCommission: number;
  platformCommission: number;
  offerAbsorbed: number;
  hasOffer: boolean;
  netPayable: number;
}

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const formatAccountantCurrency = (amount: number): string =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(toNumber(amount));

export const resolveOfferBreakdown = (entry: OfferBreakdownEntry): OfferBreakdown => {
  const metadata = (entry.metadata || {}) as Record<string, unknown>;
  const customerPaid = toNumber(entry.grossAmount);
  const commissionRate = toNumber(entry.commissionRate);

  let listPrice = toNumber(entry.mrp ?? metadata.mrp ?? metadata.originalAmountBeforeDiscount);
  let offerDiscount = toNumber(entry.discountAmount ?? metadata.discountAmount);

  if (!listPrice && customerPaid > 0 && offerDiscount > 0) {
    listPrice = customerPaid + offerDiscount;
  }
  if (!offerDiscount && listPrice > customerPaid + 0.009) {
    offerDiscount = parseFloat((listPrice - customerPaid).toFixed(2));
  }

  let baseCommission = toNumber(entry.baseCommissionAmount ?? metadata.baseCommissionAmount);
  if (baseCommission <= 0 && listPrice > 0 && commissionRate > 0) {
    baseCommission = parseFloat(((listPrice * commissionRate) / 100).toFixed(2));
  }

  const platformCommission = toNumber(entry.commissionAmount);
  const offerAbsorbed = Math.max(0, parseFloat((baseCommission - platformCommission).toFixed(2)));
  const offerDiscountRate =
    toNumber(entry.discountRate) ||
    (listPrice > 0 && offerDiscount > 0
      ? parseFloat(((offerDiscount / listPrice) * 100).toFixed(2))
      : 0);

  return {
    listPrice,
    customerPaid,
    offerDiscount,
    offerDiscountRate,
    vendorCommission: baseCommission,
    baseCommission,
    platformCommission,
    offerAbsorbed,
    hasOffer: offerDiscount > 0.009,
    netPayable: toNumber(entry.netPayable),
  };
};

export const aggregateOfferBreakdown = (entries: OfferBreakdownEntry[]) => {
  return entries.reduce(
    (acc, entry) => {
      const breakdown = resolveOfferBreakdown(entry);
      acc.totalListPrice += breakdown.listPrice;
      acc.totalCustomerPaid += breakdown.customerPaid;
      acc.totalOfferDiscount += breakdown.offerDiscount;
      acc.totalVendorCommission += breakdown.baseCommission;
      acc.totalPlatformCommission += breakdown.platformCommission;
      acc.totalOfferAbsorbed += breakdown.offerAbsorbed;
      acc.totalNetPayable += breakdown.netPayable;
      if (breakdown.hasOffer) acc.offerEntryCount += 1;
      return acc;
    },
    {
      totalListPrice: 0,
      totalCustomerPaid: 0,
      totalOfferDiscount: 0,
      totalVendorCommission: 0,
      totalPlatformCommission: 0,
      totalOfferAbsorbed: 0,
      totalNetPayable: 0,
      offerEntryCount: 0,
    }
  );
};
